import React, { useEffect, useId, useRef } from 'react';
import licenseService from '../../../services/licenseService';
import { buildSubscriptionPayload } from 'utils/buildSubscriptionPayload';

const SubscriptionCardCheckoutPanel = ({
  sdk,
  amount,
  license,
  accountEmail,
  customerName,
  customerCpf,
  onSubscriptionResult,
  onError,
  changePlan = false,
}) => {
  const containerId = useId().replace(/:/g, '');
  const brickControllerRef = useRef(null);
  const licenseRef = useRef(license);
  const customerNameRef = useRef(customerName);
  const customerCpfRef = useRef(customerCpf);
  const changePlanRef = useRef(changePlan);
  const onSubscriptionResultRef = useRef(onSubscriptionResult);
  const onErrorRef = useRef(onError);

  licenseRef.current = license;
  customerNameRef.current = customerName;
  customerCpfRef.current = customerCpf;
  changePlanRef.current = changePlan;
  onSubscriptionResultRef.current = onSubscriptionResult;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!sdk || !amount || !accountEmail) return undefined;

    let cancelled = false;
    const name = customerNameRef.current.trim();
    const [firstName, ...rest] = name.split(/\s+/);
    const lastName = rest.join(' ') || firstName;

    const mountBrick = async () => {
      try {
        const bricksBuilder = sdk.bricks();
        const controller = await bricksBuilder.create('cardPayment', containerId, {
          initialization: {
            amount: Number(amount),
            payer: {
              email: accountEmail,
              firstName,
              lastName,
              identification: {
                type: 'CPF',
                number: customerCpfRef.current,
              },
            },
          },
          callbacks: {
            onReady: () => {},
            onSubmit: async (cardFormData) => {
              try {
                const payload = buildSubscriptionPayload({
                  license: licenseRef.current,
                  customerName: customerNameRef.current,
                  customerCpf: customerCpfRef.current,
                });
                payload.card = {
                  token: cardFormData.token,
                  payment_method_id: cardFormData.payment_method_id,
                  installments: 1,
                  issuer_id: cardFormData.issuer_id,
                };

                const response = changePlanRef.current
                  ? await licenseService.changeSubscriptionPlan(payload)
                  : await licenseService.createSubscription(payload);

                if (!response?.success) {
                  const message = response?.error || 'Erro ao criar assinatura';
                  onErrorRef.current?.(message);
                  throw new Error(message);
                }

                // Show our result UI before Brick finishes its loading state.
                onSubscriptionResultRef.current?.(response);
              } catch (error) {
                const message =
                  error?.message || 'Erro ao criar assinatura';
                onErrorRef.current?.(message);
                throw error;
              }
            },
            onError: (error) => {
              onErrorRef.current?.(error?.message || 'Erro no formulário de cartão');
            },
          },
        });

        if (!cancelled) {
          brickControllerRef.current = controller;
        } else {
          controller?.unmount?.();
        }
      } catch (error) {
        if (!cancelled) {
          onErrorRef.current?.(
            error.message || 'Não foi possível carregar o formulário de cartão'
          );
        }
      }
    };

    mountBrick();

    return () => {
      cancelled = true;
      brickControllerRef.current?.unmount?.();
      brickControllerRef.current = null;
    };
    // Remount only when checkout identity changes — not on every keystroke.
  }, [sdk, amount, accountEmail, containerId]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Cobrança mensal automática no cartão de crédito via Mercado Pago. Você pode cancelar
        a qualquer momento e manter o acesso até o fim do período já pago.
      </p>
      <p className="text-xs text-muted-foreground">
        A primeira cobrança no cartão pode levar até 2 horas para aparecer no extrato e no
        painel do Mercado Pago após a confirmação da assinatura.
      </p>
      <div id={containerId} />
    </div>
  );
};

export default SubscriptionCardCheckoutPanel;
